import * as React from 'react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu as MenuIcon, Search } from 'lucide-react';
import styled from 'styled-components';

import { MOBILE_HEADER_HEIGHT } from 'components/constants/breakpoints';
import { T } from 'services/ThemeTokens';
import {
  MobileIconButton,
  mobileIconButtonCss,
} from 'molecules/MobileIconButton';

export interface IMobileHeader {
  /** Название текущего раздела (пункт меню страницы). */
  title: string;
  menuOpen: boolean;
  /** `id` листа меню — для `aria-controls` бургера. */
  menuId: string;
  onOpenMenu: () => void;
  onOpenPalette: () => void;
  /** Колокольчик — слотом, как у компьютерной шапки. */
  notifications?: ReactNode;
  userFirstName?: string | null;
  userLastName?: string | null;
  userEmail?: string | null;
  onOpenProfile?: () => void;
}

const initialsOf = (
  first?: string | null,
  last?: string | null,
  email?: string | null,
): string => {
  const fromName = [first, last]
    .map((part) => (part || '').trim().charAt(0))
    .join('');
  return (fromName || (email || '').charAt(0)).toUpperCase();
};

/**
 * ТЗ 0121 — шапка ERP на телефоне (≤ 767 px): бургер, название раздела,
 * поиск (командная палитра — Cmd+K на телефоне нет), колокольчик и аватар.
 * Тема, язык и выход — в листе меню. Компьютерная шапка (`Menu.tsx`) не
 * меняется: эту рисует контейнер только при `useIsMobile()`.
 */
export const MobileHeader = ({
  title,
  menuOpen,
  menuId,
  onOpenMenu,
  onOpenPalette,
  notifications,
  userFirstName,
  userLastName,
  userEmail,
  onOpenProfile,
}: IMobileHeader) => {
  const { t } = useTranslation();
  const fullName = [userFirstName, userLastName].filter(Boolean).join(' ');
  const who = fullName || userEmail || '';
  const initials = initialsOf(userFirstName, userLastName, userEmail);

  return (
    <Bar data-testid="mobile-header">
      <MobileIconButton
        icon={MenuIcon}
        onClick={onOpenMenu}
        aria-label={t('mobile.header.openMenu')}
        aria-haspopup="dialog"
        aria-expanded={menuOpen}
        aria-controls={menuId}
      />
      {/* Не `<h1>`: заголовок `h1` у каждой страницы свой, здесь — название
          раздела уровнем ниже. Сюда возвращается фокус, когда меню-лист
          закрылся переходом. */}
      <Title role="heading" aria-level={2} tabIndex={-1} data-page-heading>
        {title}
      </Title>
      <Actions>
        <MobileIconButton
          icon={Search}
          onClick={onOpenPalette}
          aria-label={t('mobile.header.search')}
          title={t('mobile.header.search')}
        />
        {notifications}
        {who ? (
          <Avatar
            type="button"
            onClick={onOpenProfile}
            disabled={!onOpenProfile}
            aria-label={t('profile.openFor', { name: who })}
            title={who}
          >
            {initials}
          </Avatar>
        ) : null}
      </Actions>
    </Bar>
  );
};

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: ${MOBILE_HEADER_HEIGHT}px;
  min-width: 0;
  box-sizing: border-box;
  background: ${T.surface};
`;

const Title = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 4px;
  overflow: hidden;
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
  color: ${T.text};
  white-space: nowrap;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
  }
`;

const Actions = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 2px;
`;

/** Аватар — та же зона 40 × 40 и кольцо фокуса, что у кнопок-значков. */
const Avatar = styled.button`
  ${mobileIconButtonCss}
  margin-left: 2px;
  border-radius: 50%;
  background: ${T.accentSoft};
  color: ${T.accentText};
  font: inherit;
  font-size: 14px;
  font-weight: 700;

  &:hover,
  &:active {
    background: ${T.accentSoft};
    color: ${T.accentText};
  }

  &:disabled {
    opacity: 1;
  }
`;
